/* eslint-disable react/prop-types */
/* eslint-disable import/no-cycle */
/* eslint-disable jsx-a11y/anchor-is-valid */

import React from 'react';
import styled from 'styled-components';

import { object as YupObject, string as YupString } from 'yup';
import { Formik } from 'formik';

import Input from '@ntuctech/devex-tangram/Input';
import ButtonContained from '@ntuctech/devex-tangram/Button/ButtonContained';
import Link from '@ntuctech/devex-tangram/Link';

import { useAuth0 } from '../../Auth0Provider';
import { useFlowControl } from '../../FlowControlProvider';

import { SCHEMA } from '../../../constants';

import PageContent from '../../theme/PageContent';
import PageHeader from '../../theme/PageHeader';
import UserTag from '../../theme/UserTag';
import FormWrapper from '../../theme/FormWrapper';
import FormElement from '../../theme/FormElement';
import FormActions from '../../theme/FormActions';
import ErrorBanner from '../../theme/ErrorBanner';
import BackButtonDesktop from '../../theme/BackButtonDesktop';

import Logger from '../../../services/logger';

const gaScreenName = 'SignIn - Email';

const ForgotLink = styled(Link)`
  display: inline-block;
`;
const isPasswordError = (e) => e && e.description === 'Wrong email or password';

const FormContent = (props) => {
  const { trackPwl } = useAuth0();
  const {
    values,
    errors,
    handleChange,
    handleBlur,
    touched,
    isValid,
    dirty,
    isSubmitting,
    submitForm,
    passwordError,
  } = props;

  const { goNext } = useFlowControl();
  const [isInitiated, setIsInitiated] = React.useState(false);

  const { email, password } = values;

  const showErrorFor = (fieldName) => touched[fieldName] && Boolean(errors[fieldName]);

  const showPasswordError = showErrorFor('password');

  const isSubmitButtonDisabled = !dirty || !isValid;

  const handleForgot = React.useCallback(
    (e) => {
      e.preventDefault();
      goNext();
    },
    [goNext],
  );

  const handleKeyPress = React.useCallback(
    (event) => {
      if (event.key === 'Enter' && !isSubmitButtonDisabled) {
        event.target.blur();
        submitForm();
      }
    },
    [submitForm, isSubmitButtonDisabled],
  );

  const handleFocus = React.useCallback(() => {
    if (passwordError) {
      trackPwl({
        screen_name: gaScreenName,
        event_action: 'Click',
        event_label: 'SignIn_Passwd_Error_Initiated',
        origin_target: 'password',
      });
    } else if (!isInitiated) {
      trackPwl({
        screen_name: gaScreenName,
        event_action: 'Fill In',
        event_label: 'SignIn_Email_Initiated',
        origin_target: 'password',
      });
      setIsInitiated(true);
    }
  }, [passwordError, isInitiated, setIsInitiated, trackPwl]);

  return (
    <FormWrapper>
      <FormElement hidden>
        <Input
          id="signInInputHiddenEmail"
          data-testid="signInInputHiddenEmail"
          type="email"
          name="email"
          label="Email"
          value={email}
          onChange={handleChange}
          onBlur={handleBlur}
          required
          fullWidth
          autoComplete="username"
        />
      </FormElement>
      <FormElement compact>
        <Input
          id="signInInputPassword"
          data-testid="signInInputPassword"
          type="password"
          name="password"
          label="Password"
          value={password}
          helpText={showPasswordError ? errors.password : ''}
          error={showPasswordError}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyPress={handleKeyPress}
          required
          fullWidth
          autoComplete="current-password"
        />
      </FormElement>
      <FormElement spacing={{ mt: 0.5, mb: 3 }}>
        <ForgotLink
          onClick={handleForgot}
          size="m"
          mode="light"
          href="#"
          data-testid="signInLinkForgotPassword"
        >
          Forgot password
        </ForgotLink>
      </FormElement>
      <FormActions>
        <ButtonContained
          data-testid="signInButtonSubmit"
          fullWidth
          compact
          disabled={isSubmitButtonDisabled}
          loading={isSubmitting}
          onClick={submitForm}
        >
          Log in
        </ButtonContained>
      </FormActions>
    </FormWrapper>
  );
};

const LoginSchema = YupObject().shape({
  password: YupString().trim().required(SCHEMA.passwordLogin.emptyPasswordMsg),
});

const EmailLogIn = () => {
  const { login, userContext, trackPwl } = useAuth0();
  const { goPrev } = useFlowControl();
  const [error, setError] = React.useState(null);
  const [needsReattempt, setNeedsReattempt] = React.useState(false);

  React.useEffect(() => {
    trackPwl({
      screen_name: gaScreenName,
      event_action: 'Landed',
      event_label: 'SignIn_Email',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userEmail = userContext.email || '';

  const initialValues = { email: userEmail, password: '' };

  const onSubmit = async (values, { resetForm, setSubmitting }) => {
    trackPwl({
      screen_name: gaScreenName,
      event_action: 'Click',
      event_label: needsReattempt ? 'SignIn_Passwd_Error_Login' : 'SignIn_Email_Login',
    });

    setError(null);
    const apiPromise = new Promise((resolve, reject) => {
      login({ ...values }, (err) => {
        setSubmitting(false);
        if (err) {
          reject(err);
        }
        resolve();
      });
    });

    try {
      await apiPromise;

      resetForm();
    } catch (e) {
      setError(e);
      Logger.error(e);

      if (isPasswordError(e)) {
        if (!needsReattempt) {
          setNeedsReattempt(true);
        }
        trackPwl({
          screen_name: gaScreenName,
          event_action: 'Landed',
          event_label: 'SignIn_Passwd_Error_Invalid',
        });
      }
    }
  };

  return (
    <>
      <BackButtonDesktop action={goPrev} />
      <PageContent>
        <PageHeader header="Log in" backAction={goPrev} />
        {userEmail && <UserTag content={userEmail} data-testid="tag-userEmail" />}
        {error && <ErrorBanner error={error} />}
        <Formik
          initialValues={initialValues}
          enableReinitialize
          onSubmit={onSubmit}
          validationSchema={LoginSchema}
        >
          {(formikProps) => <FormContent {...formikProps} passwordError={isPasswordError(error)} />}
        </Formik>
      </PageContent>
    </>
  );
};

export default EmailLogIn;
